import { memo, useCallback, useRef, useState } from "react";

import { api } from "@/shared/api/api";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { handleError } from "@/shared/lib/helpers/utils";
import { Input } from "@/shared/ui/Input";

import { UserData } from "../../model/types/userData";

import cls from "./ClientNotes.module.scss";

interface ClientNotesProps {
  data: UserData;
  onSuccess?: () => void;
}

export const ClientNotes = memo(({ data, onSuccess }: ClientNotesProps) => {
  const { showAlert } = useAlert();

  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>(data.notes || "");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const originalNotesRef = useRef<string>(data.notes || "");

  const onClickEdit = useCallback(async () => {
    if (!isDisabled) {
      const hasChanged = notes !== originalNotesRef.current;

      if (hasChanged) {
        setIsSaving(true);

        try {
          await api.patch(`/api/users/${data.id}`, {
            notes,
          });

          showAlert({
            theme: AlertTheme.SUCCESS,
            message: "Notes saved successfully",
          } as AlertOptions);

          originalNotesRef.current = notes;

          if (onSuccess) {
            onSuccess();
          }
        } catch (err) {
          const error = handleError(err);

          showAlert({
            theme: AlertTheme.ERROR,
            message: error.details,
          } as AlertOptions);
        } finally {
          setIsSaving(false);
        }
      }
    }

    setDisabled((value) => !value);
  }, [isDisabled, notes, data.id, onSuccess, showAlert]);

  const onChangeNotes = useCallback((value: string) => {
    setNotes(value);
  }, []);

  return (
    <div className={cls.ClientNotes}>
      <div className={cls.InputWrapper}>
        <Input
          className={cls.InputField}
          disabled={isDisabled}
          value={notes}
          onChange={onChangeNotes}
          placeholder="Add notes about this client..."
          rows={4}
        />
      </div>
      <button type="button" className={cls.EditBtn} onClick={onClickEdit} disabled={isSaving} />
    </div>
  );
});
